/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';

import { urlFiltersToCioFacets } from '~/grid/utils/cioUtils';

import useGridInputs, { GRID_TYPES } from './useGridInputs';
import useUnifiedProductGridData from './useUnifiedProductGridData';

const SelectedFacetsToApplyContext = React.createContext();

const useSelectedFacetsToApply = () => {
  const context = React.useContext(SelectedFacetsToApplyContext);

  if (!context) {
    throw new Error(
      'useSelectedFacetsToApply must be used within a SelectedFacetsToApplyProvider'
    );
  }

  return context;
};

/**
 * This provider acts as a state manager for the queue of selected facets
 * that the user wants to apply to the URL. This is useful for when the user
 * wants to select multiple facets before applying them all at once.
 */
const SelectedFacetsToApplyProvider = ({ children }) => {
  const [selectedFacetsToApply, setSelectedFacetsToApply] = useState({});
  const { selectedUrlFilters } = useUnifiedProductGridData();
  const { gridType } = useGridInputs();

  // Parse the url params and save them in the state
  // This state needs to be managed so that the user can select multiple facets
  // before applying them and updating the url all at the same time
  useEffect(() => {
    const newSelectedFacetsToApply = Object.entries(selectedUrlFilters).reduce(
      (acc, [facetName, facetOptions]) => {
        const mappedFacetName =
          gridType === GRID_TYPES.PRODUCTS_V2_GRID
            ? facetName
            : urlFiltersToCioFacets[facetName] || facetName;
        return { ...acc, [mappedFacetName]: facetOptions };
      },
      {}
    );
    setSelectedFacetsToApply(newSelectedFacetsToApply);
  }, [gridType, selectedUrlFilters]);

  const toggleSelectedFacetsToApply = ({ facetName, facetOption }) => {
    setSelectedFacetsToApply((prev) => {
      if (!prev[facetName]) {
        return {
          ...prev,
          [facetName]: [facetOption],
        };
      }

      // If option is already selected, remove it
      // If option is not selected, add it
      const isFacetOptionSelected = prev[facetName].some(
        (option) => String(option) === String(facetOption)
      );
      const newFacetOptions = isFacetOptionSelected
        ? prev[facetName].filter(
            (option) => String(option) !== String(facetOption)
          )
        : [...prev[facetName], facetOption];

      const newSelectedFacetsToApply = {
        ...prev,
        [facetName]: newFacetOptions,
      };

      return newSelectedFacetsToApply;
    });
  };

  const isFacetOptionSelected = ({ facetName, facetOption }) => {
    return Boolean(
      selectedFacetsToApply[facetName]?.some(
        (option) => String(option) === String(facetOption)
      )
    );
  };

  // we need to set the selectedFacetsToApply to an empty array
  // so that `getNewQueryParamString` can remove the query param from the URL
  const clearAllSelectedFacetsToApply = (additionalFilters = []) => {
    const newSelectedFacetsToApply = Object.keys(selectedFacetsToApply).reduce(
      (acc, facetName) => ({ ...acc, [facetName]: [] }),
      {}
    );
    additionalFilters.forEach((name) => {
      newSelectedFacetsToApply[name] = [];
    });
    setSelectedFacetsToApply(newSelectedFacetsToApply);
  };

  return (
    <SelectedFacetsToApplyContext.Provider
      value={{
        selectedFacetsToApply,
        setSelectedFacetsToApply,
        toggleSelectedFacetsToApply,
        isFacetOptionSelected,
        clearAllSelectedFacetsToApply,
      }}
    >
      {children}
    </SelectedFacetsToApplyContext.Provider>
  );
};

export default useSelectedFacetsToApply;
export { SelectedFacetsToApplyProvider };
